import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Body2,
  Button,
  ConfirmDialog,
  Dialog,
  EditableText,
  IconButton,
  Input,
  Label,
  Tooltip,
  TrashIcon,
} from "@activecollab/components";
import { ReorderableList } from "@activecollab/yank-and-drop";
import classNames from "classnames";
import { Field, FieldArray, Form, Formik } from "formik";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import * as yup from "yup";
import { DraggableSuggestionItem } from "~/components/tasks/SuggestWizard/DraggableSuggestionItem";
import { NEW } from "~/components/tasks/SuggestWizard/suggestionUtils";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import { FocusOnFirstError } from "~/pages/GettingStarted/SpecifyRateCardDialog";
import { useCompleteStepMutation } from "~/repository/useGettingStarted";
import { ADD_PROJECT_FROM_DATASET_SUCCESS } from "~/store/action_types";
import { addProjectFromDataSet } from "~/store/actions/actions.projects";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions";
import Angie from "~/utils/angie";

const DirtyState = ({ setFormState, dirty }) => {
  useEffect(() => {
    if (dirty) {
      setFormState(true);
    }
  }, [dirty, setFormState]);

  return null;
};

DirtyState.propTypes = {
  setFormState: PropTypes.func,
  dirty: PropTypes.bool,
};

const formValidator = yup.object({
  projectName: yup.string().required("Please enter a project name"),
  taskLists: yup
    .array()
    .of(
      yup.object({
        name: yup.string().trim(),
        tasks: yup.array().of(
          yup.object({
            name: yup.string().trim(),
          })
        ),
      })
    )
    .test("tasklist-and-task-validation", "At least one task list and one task must have a name", function (taskLists) {
      if (!Array.isArray(taskLists) || taskLists.length === 0) {
        return false;
      }

      const validTaskListIndex = taskLists.findIndex((tl) => tl.name && tl.name.trim() !== "");

      if (validTaskListIndex === -1) {
        const firstEmptyIndex = taskLists.findIndex((tl) => !tl.name || tl.name.trim() === "");
        if (firstEmptyIndex >= 0) {
          return this.createError({
            path: `taskLists[${firstEmptyIndex}].name`,
            message: "At least one task list must have a name",
          });
        }
        return false;
      }

      const allTasks = taskLists.flatMap((tl) => tl.tasks || []);
      const validTaskIndex = allTasks.findIndex((t) => t.name && t.name.trim() !== "");

      if (validTaskIndex === -1) {
        const firstEmptyTaskIndex = taskLists
          .map(
            (tl, i) =>
              tl.tasks?.map((t, j) => ({
                tlIndex: i,
                taskIndex: j,
                name: t.name,
              })) || []
          )
          .flat()
          .find((t) => !t.name || t.name.trim() === "");

        if (firstEmptyTaskIndex) {
          return this.createError({
            path: `taskLists[${firstEmptyTaskIndex.tlIndex}].tasks[${firstEmptyTaskIndex.taskIndex}].name`,
            message: "At least one task must have a name",
          });
        }
        return false;
      }

      return true;
    }),
});

export const ProjectForm = ({ projectName, taskLists, onClose, setFormState, isAlreadyCompleted, setProjectId }) => {
  const { t } = useTranslation();
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const completeStepMutation = useCompleteStepMutation();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [taskListForDelete, setTaskListForDelete] = useState("");
  const estimatesEnabled = useSelector((state) => getRawConfigOption(state, "estimates_enabled"));
  const defaultProjectTaskEstimatesEnabled = useSelector((state) =>
    getRawConfigOption(state, "default_project_task_estimates_enabled")
  );

  const createNewTaskList = useCallback(() => {
    return { id: v4(), name: t("New Task List"), tasks: [], is_add_new: true };
  }, [t]);

  const deleteTaskList = useCallback(() => {
    formikRef.current?.setFieldValue(
      "taskLists",
      [...formikRef.current.values.taskLists].filter((taskList) => taskList.id !== taskListForDelete)
    );
    setTaskListForDelete("");
    setOpenConfirmDialog(false);
  }, [taskListForDelete]);

  const closeConfirmDialog = useCallback(() => setOpenConfirmDialog(false), [setOpenConfirmDialog]);

  return (
    <div className="lg:tw-max-w-[500px] tw-min-h-[300px]">
      <div className="tw-flex tw-flex-col tw-w-full tw-min-w-[426px]">
        <Formik
          initialValues={{
            projectName: projectName,
            taskLists: [...taskLists, createNewTaskList()],
          }}
          onSubmit={({ projectName, taskLists }) => {
            return dispatch(
              addProjectFromDataSet({
                project_name: projectName,
                task_lists: taskLists
                  .filter((taskList) => !taskList.is_add_new)
                  .map((taskList) => {
                    return {
                      ...taskList,
                      tasks: taskList.tasks
                        .filter((task) => task?.name?.trim() !== "")
                        .map(({ variant, ...rest }) => rest),
                    };
                  }),
              })
            )
              .then((r) => {
                if (r.type === ADD_PROJECT_FROM_DATASET_SUCCESS) {
                  Angie.logger.info(
                    "Getting started step completed.",
                    { step: "create_first_project", reopen: isAlreadyCompleted ? 1 : 0 },
                    true
                  );
                  completeStepMutation.mutate("getting_started_project");
                  showSuccessToast("Project created successfully.");
                  if (setProjectId) {
                    setProjectId(r?.response?.result?.project);
                  }

                  return onClose(false);
                }

                showErrorToast(t("There was an error creating the project."));
              })
              .catch(() => {
                showErrorToast(t("There was an error creating the project."));
              });
          }}
          innerRef={formikRef}
          validationSchema={formValidator}
        >
          {({ values, isSubmitting, errors, submitCount, dirty, setFieldValue }) => (
            <Form>
              <div className="tw-flex tw-flex-col tw-w-full">
                <div className="tw-mt-2 lg:tw-mt-0 lg:tw-h-full tw-h-full">
                  <div className="tw-flex tw-gap-4 tw-justify-between">
                    <Field name="projectName">
                      {({ field }) => (
                        <div className="tw-flex-1">
                          <Label
                            size="small"
                            htmlFor="project-name"
                            className="tw-mt-4"
                          >
                            {t("Project Name*")}
                          </Label>
                          <Input
                            value={field.value}
                            className="tw-mr-4"
                            autoFocus
                            invalid={errors?.projectName && submitCount}
                            {...field}
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                  <>
                    <FieldArray name="taskLists">
                      {({ replace, remove, push: addList }) => {
                        return (
                          <>
                            <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-2">
                              {values.taskLists.map((taskList, i) => (
                                <div
                                  className="tw-flex tw-flex-col"
                                  key={`${taskList.id}-${i}`}
                                >
                                  <div
                                    className={classNames(
                                      "tw-flex tw-justify-between tw-group tw-w-full tw-items-center tw-h-8 tw-rounded-md",
                                      { "hover:tw-bg-theme-200": !taskList.is_add_new }
                                    )}
                                  >
                                    <EditableText
                                      className="tw-mr-4 -tw-ml-1"
                                      value={taskList?.name}
                                      variant="Body 2"
                                      weight={taskList.is_add_new ? "light" : "bold"}
                                      onSave={(e) => {
                                        if (taskList.is_add_new) {
                                          replace(i, {
                                            ...values.taskLists[i],
                                            name: e?.target?.value,
                                            is_add_new: false,
                                          });
                                          return addList(createNewTaskList());
                                        }
                                        replace(i, { ...values.taskLists[i], name: e?.target?.value });
                                      }}
                                    />
                                    {!taskList.is_add_new && values.taskLists.length > 2 ? (
                                      <Tooltip title={t("Delete Task List")}>
                                        <IconButton
                                          type="button"
                                          variant="text gray"
                                          className="tw-flex tw-opacity-0 group-hover:tw-opacity-100"
                                          size="small"
                                          onClick={() => {
                                            if (taskList.tasks.length === 0) {
                                              return remove(i);
                                            }
                                            setOpenConfirmDialog(true);
                                            setTaskListForDelete(taskList.id);
                                          }}
                                        >
                                          <TrashIcon />
                                        </IconButton>
                                      </Tooltip>
                                    ) : null}
                                  </div>
                                  <FieldArray name={`taskLists[${i}].tasks`}>
                                    {({ remove, push, move }) => {
                                      return (
                                        <>
                                          <ReorderableList
                                            className="-tw-mx-8"
                                            onMove={(from, to) => move(from, to)}
                                            dragAxis="y"
                                            handle=".drag-handle"
                                          >
                                            {values.taskLists[i].tasks.map((item, ti) => (
                                              <DraggableSuggestionItem
                                                key={item.id}
                                                disableRemove={
                                                  values.taskLists?.flatMap((tl) => tl.tasks || [])?.length < 2
                                                }
                                                id={item.id}
                                                projectId={1}
                                                onRemove={() => remove(ti)}
                                                item={item}
                                                inputName={`taskLists.${i}.tasks.${ti}.name`}
                                                invalid={
                                                  !!(submitCount > 0 && errors.taskLists?.[i]?.tasks?.[ti]?.name)
                                                }
                                                onUpdate={(id, field, value) =>
                                                  setFieldValue(`taskLists[${i}].tasks[${ti}][${field}]`, value)
                                                }
                                                shouldShowEstimate={
                                                  estimatesEnabled && defaultProjectTaskEstimatesEnabled
                                                }
                                                allowAllUsers
                                                selectionMode="custom"
                                              />
                                            ))}
                                          </ReorderableList>
                                          {!values.taskLists[i].is_add_new ? (
                                            <Body2
                                              color="theme-primary"
                                              onClick={() => {
                                                push({
                                                  name: "",
                                                  id: v4(),
                                                  variant: NEW,
                                                  estimate: "",
                                                  job_type_id: null,
                                                });
                                              }}
                                              className="tw-mt-1 tw-cursor-pointer"
                                            >
                                              {t("Add Task to This List")}
                                            </Body2>
                                          ) : null}
                                        </>
                                      );
                                    }}
                                  </FieldArray>
                                </div>
                              ))}
                            </div>
                          </>
                        );
                      }}
                    </FieldArray>
                  </>
                </div>
                <Dialog.ContentDivider className="tw-mt-4" />
                <Dialog.Actions className="tw-pl-0 tw-pb-0">
                  <Button
                    type="submit"
                    variant="primary"
                    className="tw-mr-3"
                    disabled={isSubmitting}
                  >
                    {t("Save")}
                  </Button>
                  <Button
                    type="button"
                    onClick={onClose}
                    variant="secondary"
                    disabled={isSubmitting}
                  >
                    {t("Cancel")}
                  </Button>
                </Dialog.Actions>
                <FocusOnFirstError />
                <DirtyState
                  dirty={dirty}
                  setFormState={setFormState}
                />
              </div>
            </Form>
          )}
        </Formik>
        {openConfirmDialog ? (
          <ConfirmDialog
            open={openConfirmDialog}
            dialogTitle={t("Delete Task List")}
            dialogContent={t("Deleting this task list will also remove all its tasks.")}
            confirmBtnText={t("Delete")}
            onCancel={closeConfirmDialog}
            onConfirm={deleteTaskList}
          />
        ) : null}
      </div>
    </div>
  );
};

ProjectForm.propTypes = {
  projectName: PropTypes.string,
  taskLists: PropTypes.array,
  onClose: PropTypes.func,
  setFormState: PropTypes.func,
  isAlreadyCompleted: PropTypes.bool,
  setProjectId: PropTypes.func,
};

ProjectForm.defaultProps = {
  projectName: "",
  taskLists: [],
};
